/**
 * Capture app — "Create Document" workflow, ported from Noteble's Add page
 * (ADR-022): a 2-step wizard (Add Items → Templates) with multi-element
 * capture. The user accumulates any mix of voice transcripts, photos, notes,
 * AI draft requests, and uploaded documents as discrete captured items;
 * everything persists locally (IndexedDB via AddContext + drafts, flushed on
 * Next), then one or more templates are selected and ONE DOCUMENT PER
 * TEMPLATE is assembled in parallel by the AI backend
 * (POST /ai/assemble-document — Claude analyses all captured elements,
 * photos included as vision blocks, and fills the template).
 *
 * Record captures voice via useVoiceCapture — browser-direct AWS Transcribe
 * when the media Identity Pool is configured, else the browser Web Speech
 * API — accumulating a live transcript that becomes a captured item on stop.
 * Photos upload via the media presigner and are analyzed by the
 * Claude-vision job at capture time (AddContext).
 */

import { useState, useEffect, useMemo, useRef, useCallback, type ComponentType } from 'react';
import {
  ArrowRight, Sparkles, Clock,
  Mic, Image as ImageIcon, Upload, Check, Eye, Pencil, Plus, ExternalLink,
  StickyNote, AlertCircle, X, CheckCircle2, XCircle,
} from 'lucide-react';
import { DocumentModal } from '@puglieseweb/shared-react-ui';
import { Spinner, LoadingState } from '@systemdox/ui';
import { useNotifications } from '@puglieseweb/shared-react-notifications';
import { useTenant } from '@/contexts/TenantContext';
import { cn } from '@/lib/utils';
import { createSystemdoxCaptureHost, consoleDocumentPath } from '@/capture/captureHost';
import { useConsoleUrl } from '@/hooks/useConsoleUrl';
import { useProject } from '@/contexts/ProjectContext';
import { appendTranscript } from '@/capture/useDictation';
import { useVoiceCapture } from '@/capture/useVoiceCapture';
import { isSupportedImage, downscaleImage, buildMediaSection } from '@/capture/photoCapture';
import { aiApi, type AssembleAttachment } from '@/services/aiApi';
import { notificationsApi, type CreateNotificationInput } from '@/services/notificationsApi';
import { createLogger } from '@/lib/logger';
import { templatesApi, type TemplateMetadata, type TemplateDocType } from '@/services/templatesApi';
import { templateIcon, templateColor } from '@/capture/templateVisuals';
import { TemplateModal } from '@/capture/TemplateModal';
import { useAdd, type CapturedPhoto, type RecordingNote } from '@/capture/AddContext';
import {
  useAddWorkflow, canAdvanceFromCapture,
  GENERIC_TEMPLATE, isGenericTemplate,
  type CapturedStream, type InputMethod, type WorkflowStep,
} from '@/capture/workflow';
import { CapturedItemsPanel, AI_DRAFT_TITLE } from '@/capture/CapturedItemsPanel';
import { PhotoCapturePanel } from '@/capture/PhotoCapturePanel';
import { MyDraftsPanel } from '@/capture/MyDraftsPanel';
import { ListItemCard } from '@/capture/ListItemCard';
import { useDrafts } from '@/capture/useDrafts';
import { useAutoSaveDraft, buildDraftItems, PENDING_NOTE_ID } from '@/capture/useAutoSaveDraft';

const log = createLogger('CaptureAddWorkflow');

/**
 * Store a generation result so it survives a reload and shows up in the
 * /notifications history.
 *
 * Returns the stored id, or null when persistence failed. There is deliberately
 * NO ephemeral twin: an in-memory notification carries its own id, so
 * dismissing the visible copy left the stored one behind to reappear on the
 * next load. The one stored record is the record — it reaches the bell through
 * the provider's poller, keyed on the same job id.
 */
async function persistNotification(input: CreateNotificationInput): Promise<string | null> {
  try {
    return (await notificationsApi.create(input)).id;
  } catch (err) {
    log.error('Failed to persist notification', err as Error);
    return null;
  }
}

const STEPS: { key: WorkflowStep; label: string }[] = [
  { key: 'capture', label: 'Capture' },
  { key: 'template', label: 'Templates' },
];

interface MethodDef {
  key: InputMethod;
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number | string }>;
  bg: string;
  fg: string;
  hover: string;
}

const METHODS: MethodDef[] = [
  { key: 'voice', label: 'Record', icon: Mic, bg: 'bg-red-100 dark:bg-red-900/40', fg: 'text-red-600 dark:text-red-400', hover: 'hover:bg-red-200 dark:hover:bg-red-900/60' },
  { key: 'photo', label: 'Photo', icon: ImageIcon, bg: 'bg-emerald-100 dark:bg-emerald-900/40', fg: 'text-emerald-600 dark:text-emerald-400', hover: 'hover:bg-emerald-200 dark:hover:bg-emerald-900/60' },
  { key: 'ask-ai', label: 'Ask AI', icon: Sparkles, bg: 'bg-violet-100 dark:bg-violet-900/40', fg: 'text-violet-600 dark:text-violet-400', hover: 'hover:bg-violet-200 dark:hover:bg-violet-900/60' },
  { key: 'text', label: 'Note', icon: StickyNote, bg: 'bg-amber-100 dark:bg-amber-900/40', fg: 'text-amber-600 dark:text-amber-400', hover: 'hover:bg-amber-200 dark:hover:bg-amber-900/60' },
  { key: 'upload', label: 'Upload', icon: Upload, bg: 'bg-blue-100 dark:bg-blue-900/40', fg: 'text-blue-600 dark:text-blue-400', hover: 'hover:bg-blue-200 dark:hover:bg-blue-900/60' },
];

const GROUP_ORDER: TemplateDocType[] = ['requirement', 'design'];
const GROUP_LABEL: Record<TemplateDocType, string> = {
  requirement: 'Requirements',
  design: 'Design',
};

// Text/PDF/image files the Upload button accepts (no audio — voice is
// transcribed live in the browser).
const UPLOAD_ACCEPT = '.md,.markdown,.txt,.adoc,.rtf,.pdf,.jpg,.jpeg,.png,.webp';

type ModalState = { mode: 'view' | 'create'; templateId?: string; startEditing?: boolean };

/** In-place viewer/editor state for notes, AI drafts, transcripts, photos. */
interface ViewerState {
  kind: 'note' | 'ai' | 'transcript' | 'photo';
  refId: string | null; // element id; null = new AI draft
  title: string;
  subtitle: string;
  content: string;
  dirty: boolean;
  readOnly: boolean;
}

/** One row in the generation progress panel — a template becoming a document. */
interface GenJob {
  templateId: string;
  templateName: string;
  docType: TemplateDocType;
  status: 'preparing' | 'generating' | 'creating' | 'done' | 'failed';
  draftId?: string;
  docName?: string;
  error?: string;
}

const AUTOSAVE_DELAY_MS = 800;
const LIVE_TRANSCRIPT_WORD_LIMIT = 200;

/**
 * All captured text elements flattened into the assembly payload, each under
 * a heading so the model can tell transcripts, notes, uploads, and AI draft
 * requests apart.
 */
export function buildTextContent(
  streams: CapturedStream[],
  notes: RecordingNote[],
  pendingNote: string,
): string {
  const parts: string[] = [];
  streams.forEach((s, i) => {
    if (s.transcript) parts.push(`## Voice transcript ${i + 1}\n\n${s.transcript}`);
  });
  notes.forEach((n) => {
    const title = n.sourceTitle || (n.sourceType === 'document' ? 'Uploaded document' : 'Note');
    parts.push(`## ${title}\n\n${n.text}`);
  });
  if (pendingNote.trim()) parts.push(`## Note\n\n${pendingNote.trim()}`);
  return parts.join('\n\n');
}

export function CaptureAddWorkflow() {
  const { tenant } = useTenant();
  const { selected: selectedProject } = useProject();
  const consoleUrl = useConsoleUrl();
  const { addJob } = useNotifications();

  /**
   * Record one generation result.
   *
   * The stored notification IS the record — the provider's poller finds it
   * under the job id and renders it in the bell, so there is no in-memory twin
   * to dismiss separately. When the job id is unknown (the assemble POST itself
   * failed, so no job was ever queued) the stored id is registered instead, so
   * the result still reaches the bell.
   */
  async function announce(input: CreateNotificationInput, jobId?: string): Promise<void> {
    const id = await persistNotification({ ...input, ...(jobId ? { jobId } : {}) });
    if (id && !jobId) {
      const now = new Date().toISOString();
      addJob({ id, status: 'pending', label: input.title, createdAt: now, updatedAt: now });
    }
  }
  const {
    photos, notes, uploadStatuses,
    addPhoto, removePhoto, updatePhotoDescription, retryPhotoUpload,
    addNote, updateNote, removeNote,
    restorePhotos, restoreNotes,
    validateBeforeSubmit, ensurePhotosUploaded,
  } = useAdd();

  // A created document opens in place, in the shared viewer/editor below —
  // the full editor on the Build console stays one explicit click away.
  const [created, setCreated] = useState<{ id: string; docType: string } | null>(null);
  const host = useMemo(
    () => createSystemdoxCaptureHost({
      tenantSlug: tenant?.slug ?? null,
      projectId: selectedProject?.projectId ?? null,
      onOpenDocument: (id, docType) => setCreated({ id, docType }),
    }),
    [tenant?.slug, selectedProject?.projectId],
  );
  const fileRef = useRef<HTMLInputElement>(null);

  // Templates come from the tenant-scoped API (platform ∪ custom).
  const [templates, setTemplates] = useState<TemplateMetadata[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [modal, setModal] = useState<ModalState | null>(null);

  const loadTemplates = useCallback(() => {
    setTemplatesLoading(true);
    templatesApi.list()
      .then((list) => { setTemplates(list); setTemplatesError(''); })
      .catch((e) => setTemplatesError(e instanceof Error ? e.message : 'Could not load templates'))
      .finally(() => { setTemplatesLoading(false); setTemplatesLoaded(true); });
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ── Workflow FSM (step, in-progress note, transcripts, template selection) ──
  const workflow = useAddWorkflow({ templates, templatesLoaded });
  const { ctx, dispatch } = workflow;

  // Resolve template ids restored before the template list had loaded.
  useEffect(() => {
    const pending = workflow.pendingTemplateIdsRef.current;
    if (!pending?.length || templates.length === 0) return;
    workflow.pendingTemplateIdsRef.current = null;
    const resolved = pending
      .map((id) => templates.find((t) => t.id === id))
      .filter((t): t is TemplateMetadata => !!t);
    if (resolved.length > 0) {
      const merged = [...ctx.selectedTemplates];
      for (const t of resolved) {
        if (!merged.some((m) => m.id === t.id)) merged.push(t);
      }
      workflow.setTemplates(merged);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // ── Drafts (IndexedDB) ──
  const {
    drafts, isLoading: draftsLoading, isSaving: draftSaving,
    hasAttemptedLoad: draftsHasAttemptedLoad,
    loadDrafts, saveDraft, autoSaveDraft, restoreDraft, deleteDraft, purgeAndForgetDraft, silentDeleteDraft,
  } = useDrafts();
  useEffect(() => { void loadDrafts(); }, [loadDrafts]);

  const { flush: flushDraft } = useAutoSaveDraft({
    ctx, dispatch, photos, notes, autoSaveDraft, silentDeleteDraft,
  });

  // ── Voice capture (live transcript → captured item on stop) ──
  const [liveTranscript, setLiveTranscript] = useState('');
  const [recordSeconds, setRecordSeconds] = useState(0);
  const liveTranscriptRef = useRef('');
  const recordSecondsRef = useRef(0);
  const voice = useVoiceCapture({
    onTranscript: (t) => setLiveTranscript((prev) => appendTranscript(prev, t)),
    onError: (e) => workflow.setError(e.message),
  });
  useEffect(() => { liveTranscriptRef.current = liveTranscript; }, [liveTranscript]);
  useEffect(() => { recordSecondsRef.current = recordSeconds; }, [recordSeconds]);
  useEffect(() => {
    if (!voice.isListening) return;
    const timer = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [voice.isListening]);

  const startRecording = useCallback(() => {
    setLiveTranscript('');
    setRecordSeconds(0);
    workflow.setError(null);
    if (!voice.isListening) voice.toggle();
  }, [voice, workflow]);

  const captureRecording = useCallback(() => {
    voice.stop();
    const transcript = liveTranscriptRef.current.trim();
    if (transcript) {
      dispatch({
        type: 'CAPTURE_STREAMING',
        id: crypto.randomUUID(),
        duration: recordSecondsRef.current,
        transcript,
      });
    } else {
      workflow.setError('Nothing was transcribed — try recording again, closer to the microphone.');
    }
    setLiveTranscript('');
    setRecordSeconds(0);
  }, [voice, dispatch, workflow]);

  const discardRecording = useCallback(() => {
    voice.stop();
    setLiveTranscript('');
    setRecordSeconds(0);
  }, [voice]);

  // ── Capture methods ──
  const [activePanel, setActivePanel] = useState<'photo' | 'text' | null>(null);
  const [viewer, setViewer] = useState<ViewerState | null>(null);

  const openAskAi = useCallback(() => {
    const existing = notes.find((n) => n.sourceTitle === AI_DRAFT_TITLE);
    setViewer({
      kind: 'ai',
      refId: existing?.id ?? null,
      title: 'Ask AI',
      subtitle: 'Draft request — developed into the document when you generate',
      content: existing?.text ?? '',
      dirty: false,
      readOnly: false,
    });
  }, [notes]);

  function methodEnabled(key: InputMethod): boolean {
    if (key === 'voice') return voice.isSupported;
    return true;
  }

  function pickMethod(m: MethodDef) {
    if (!methodEnabled(m.key)) return;
    workflow.setInputMethod(m.key);
    if (m.key === 'upload') { fileRef.current?.click(); return; }
    if (m.key === 'ask-ai') { openAskAi(); return; }
    if (m.key === 'voice') {
      if (voice.isListening) captureRecording();
      else startRecording();
      setActivePanel(null);
      return;
    }
    // photo / text — show inline capture panel
    setActivePanel(m.key === 'photo' ? 'photo' : 'text');
  }

  // Auto-close the inline photo panel when a new photo is captured (Noteble UX)
  const prevPhotosLen = useRef(photos.length);
  useEffect(() => {
    if (photos.length > prevPhotosLen.current) setActivePanel(null);
    prevPhotosLen.current = photos.length;
  }, [photos.length]);

  // ── Upload handling: images → photo, PDF → extracted note, text → note ──
  const handleUploadedFile = useCallback(async (file: File) => {
    const fileName = file.name || '';
    const isImage = /\.(jpg|jpeg|png|webp)$/i.test(fileName) || file.type.startsWith('image/');
    const isPdf = /\.pdf$/i.test(fileName) || file.type === 'application/pdf';

    if (isImage) {
      const problem = isSupportedImage(file);
      if (problem) { workflow.setError(problem); return; }
      const { blob } = await downscaleImage(file);
      addPhoto(blob);
    } else if (isPdf) {
      try {
        const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
        GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await getDocument({ data: arrayBuffer }).promise;
        const pages: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          pages.push(content.items.map((item) => ('str' in item ? item.str : '')).join(' '));
        }
        const text = pages.join('\n\n');
        if (text.trim()) addNote(text, 'document', fileName);
        else workflow.setError('No text could be extracted from that PDF.');
      } catch {
        workflow.setError('Could not read that PDF.');
      }
    } else {
      const text = await file.text();
      if (text.trim()) addNote(text, 'document', fileName);
    }
  }, [addPhoto, addNote, workflow]);

  // ── Viewers (note / AI draft / transcript / photo description) ──
  const openNote = useCallback((note: RecordingNote) => {
    const isAi = note.sourceTitle === AI_DRAFT_TITLE;
    setViewer({
      kind: isAi ? 'ai' : 'note',
      refId: note.id,
      title: isAi ? 'Ask AI' : (note.sourceType === 'document' ? (note.sourceTitle || 'Document') : 'Note'),
      subtitle: isAi ? 'Draft request — developed into the document when you generate' : 'Captured item',
      content: note.text,
      dirty: false,
      readOnly: false,
    });
  }, []);

  const openTranscript = useCallback((transcript: CapturedStream) => {
    if (!transcript.transcript) return;
    setViewer({
      kind: 'transcript',
      refId: transcript.id,
      title: 'Voice transcript',
      subtitle: `${Math.floor(transcript.duration / 60)}:${String(transcript.duration % 60).padStart(2, '0')}`,
      content: transcript.transcript,
      dirty: false,
      readOnly: true,
    });
  }, []);

  const openPhoto = useCallback((photo: CapturedPhoto) => {
    setViewer({
      kind: 'photo',
      refId: photo.id,
      title: 'Photo',
      subtitle: 'Description — included when your document is generated',
      content: photo.description || '',
      dirty: false,
      readOnly: false,
    });
  }, []);

  const saveViewer = useCallback((v: ViewerState) => {
    if (v.readOnly || !v.dirty) return;
    if (v.kind === 'photo') {
      if (v.refId) updatePhotoDescription(v.refId, v.content);
    } else if (v.refId) {
      if (v.content.trim()) updateNote(v.refId, v.content);
      else removeNote(v.refId);
    } else if (v.content.trim()) {
      // New AI draft request
      addNote(v.content, 'document', AI_DRAFT_TITLE);
    }
  }, [updatePhotoDescription, updateNote, removeNote, addNote]);

  // Save-on-close reads the viewer from state directly — running it inside a
  // setViewer updater would double-fire under StrictMode (updaters must be pure).
  const closeViewer = useCallback(() => {
    if (viewer) saveViewer(viewer);
    setViewer(null);
  }, [viewer, saveViewer]);

  // Photo preview lightbox
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);
  const handlePreviewPhoto = useCallback((photoId: string) => {
    const photo = photos.find((p) => p.id === photoId);
    if (!photo) return;
    setPreviewPhotoUrl(URL.createObjectURL(photo.blob));
  }, [photos]);
  const closePhotoPreview = useCallback(() => {
    if (previewPhotoUrl) URL.revokeObjectURL(previewPhotoUrl);
    setPreviewPhotoUrl(null);
  }, [previewPhotoUrl]);

  // ── Draft handlers ──
  const handleSaveDraft = useCallback(async () => {
    try {
      const items = buildDraftItems(ctx, photos, notes);
      await saveDraft(items, ctx.inputMethod, navigator.language || 'en-GB', ctx.selectedTemplates.map((t) => t.id));
      if (ctx.activeDraftId) await silentDeleteDraft(ctx.activeDraftId);
      restorePhotos([]);
      restoreNotes([]);
      workflow.startOver();
    } catch {
      workflow.setError('Could not save the draft.');
    }
  }, [ctx, photos, notes, saveDraft, silentDeleteDraft, restorePhotos, restoreNotes, workflow]);

  const handleRestoreDraft = useCallback(async (draftId: string) => {
    dispatch({ type: 'RESTORE_DRAFT_START' });
    try {
      const restored = await restoreDraft(draftId);
      restorePhotos(restored.photos.map((p) => ({
        id: crypto.randomUUID(),
        blob: p.blob,
        timestamp: p.timestamp,
        capturedAt: Date.now(),
        ...(p.description ? { description: p.description } : {}),
        ...(p.mediaId ? { mediaId: p.mediaId } : {}),
        ...(p.mermaid ? { mermaid: p.mermaid } : {}),
      })));
      const pendingNote = restored.notes.find((n) => n.id === PENDING_NOTE_ID);
      restoreNotes(restored.notes
        .filter((n) => n.id !== PENDING_NOTE_ID)
        .map((n) => ({
          id: n.id,
          text: n.text,
          timestamp: n.timestamp,
          createdAt: Date.now(),
          ...(n.sourceType ? { sourceType: n.sourceType as 'note' | 'document' } : {}),
          ...(n.sourceTitle ? { sourceTitle: n.sourceTitle } : {}),
        })));
      dispatch({
        type: 'RESTORE_DRAFT_COMPLETE',
        payload: {
          note: pendingNote?.text ?? '',
          capturedStreams: restored.streamingTranscripts.map((st, i) => ({
            id: `restored-${i}`, transcript: st.transcript, duration: st.duration,
          })),
          activeDraftId: draftId,
          step: 'capture',
        },
      });
    } catch {
      dispatch({ type: 'RESTORE_DRAFT_COMPLETE', payload: {} });
      workflow.setError('Could not restore the draft.');
    }
  }, [dispatch, restoreDraft, restorePhotos, restoreNotes, workflow]);

  // ── Step navigation ──
  const hasItems = ctx.capturedStreams.length > 0 || photos.length > 0 || notes.length > 0
    || !!ctx.note.trim() || voice.isListening;
  const isEmpty = !hasItems && !activePanel;

  async function goToTemplates() {
    if (voice.isListening) captureRecording();
    // A note still sitting in the inline panel becomes a captured item.
    if (ctx.note.trim()) {
      addNote(ctx.note.trim());
      workflow.setNote('');
    }
    const submitError = validateBeforeSubmit();
    if (submitError) { workflow.setError(submitError); return; }
    if (!canAdvanceFromCapture(ctx, photos.length > 0 || notes.length > 0 || !!ctx.note.trim())) {
      workflow.setError('Capture at least one item first.');
      return;
    }
    workflow.setError(null);
    // Requirement: clicking Next saves every captured element locally in
    // IndexedDB — flush the draft before moving on.
    await flushDraft();
    workflow.goToStep('template');
  }

  // ── Generation: one document per selected template, in parallel ──
  const [genJobs, setGenJobs] = useState<GenJob[] | null>(null);
  const [createdCount, setCreatedCount] = useState(0);

  const patchJob = useCallback((templateId: string, patch: Partial<GenJob>) => {
    setGenJobs((jobs) => jobs?.map((j) => (j.templateId === templateId ? { ...j, ...patch } : j)) ?? null);
  }, []);

  const canGenerate = ctx.selectedTemplates.length > 0 && !ctx.isSubmitting;

  async function handleGenerate() {
    if (!canGenerate) {
      workflow.setError('Select at least one template');
      return;
    }
    const submitError = validateBeforeSubmit();
    if (submitError) { workflow.setError(submitError); return; }

    const chosen = [...ctx.selectedTemplates];
    dispatch({ type: 'SUBMIT_START' });
    setCreated(null);
    setGenJobs(chosen.map((t) => ({
      templateId: t.id, templateName: t.name, docType: t.docType, status: 'preparing',
    })));
    // Make sure every photo made it to the media store (retrying failures);
    // photos that still can't upload are skipped — their descriptions are in
    // the text content either way.
    const uploaded = await ensurePhotosUploaded();
    const attachments: AssembleAttachment[] = uploaded
      .filter((p) => p.mediaId)
      .map((p) => ({ mediaId: p.mediaId!, ...(p.description ? { description: p.description } : {}) }));
    const textContent = buildTextContent(ctx.capturedStreams, notes, ctx.note);

    const results = await Promise.all(chosen.map(async (tpl): Promise<GenJob> => {
      // Set by onJobStarted below. Doubles as the notification id, so the
      // bell's spinner, the stored result and the row the user dismisses are
      // one thing rather than three.
      let jobId: string | undefined;
      try {
        let templateContent = '';
        if (!isGenericTemplate(tpl.id)) {
          templateContent = (await templatesApi.get(tpl.id)).content;
        }
        patchJob(tpl.id, { status: 'generating' });
        const doc = await aiApi.assembleDocument({
          templateName: tpl.name,
          templateContent,
          textContent,
          ...(attachments.length ? { attachments } : {}),
        }, (id) => {
          jobId = id;
          // The bell tracks the generation from here on, so it stays visible
          // after navigating away from /add. The provider polls this id and
          // turns it into the finished notification by itself.
          const now = new Date().toISOString();
          addJob({
            id, status: 'processing', label: tpl.name,
            sublabel: tpl.docType === 'design' ? 'Design' : 'Requirement',
            createdAt: now, updatedAt: now,
          });
        });

        patchJob(tpl.id, { status: 'creating' });
        const createdDoc = await host.createDoc({ name: doc.name, docType: tpl.docType });
        // Photos the model didn't reference still ship with the document.
        const missing = uploaded.filter((p) => p.mediaId && !doc.content.includes(p.mediaId));
        const content = doc.content + buildMediaSection(
          missing.map((p) => ({ mediaId: p.mediaId!, mermaid: p.mermaid ?? null })),
        );
        await host.updateDoc(createdDoc.id, { proposalContent: content });
        // Commit the fresh draft to git so it is versioned and searchable from
        // creation, and edited in the reader thereafter (ADR-033, git-source).
        // Best-effort: a no-repo tenant is a graceful no-op and never fails here.
        await host.commitDoc?.(createdDoc.id);

        const done: GenJob = {
          templateId: tpl.id, templateName: tpl.name, docType: tpl.docType,
          status: 'done', draftId: createdDoc.id, docName: doc.name,
        };
        patchJob(tpl.id, done);
        // Becomes a link to the created document once clicked. Only knowable
        // here — the assemble worker has no draftId — which is why the stored
        // notification, not the job row, carries it.
        const actionUrl = consoleDocumentPath(createdDoc.id, tpl.docType);
        const kind = tpl.docType === 'design' ? 'Design' : 'Requirement';
        await announce({ type: 'completed', title: doc.name, description: kind, actionUrl }, jobId);
        return done;
      } catch (e) {
        const failed: GenJob = {
          templateId: tpl.id, templateName: tpl.name, docType: tpl.docType,
          status: 'failed', error: e instanceof Error ? e.message : 'Generation failed',
        };
        patchJob(tpl.id, failed);
        await announce({ type: 'failed', title: tpl.name, description: failed.error }, jobId);
        return failed;
      }
    }));

    const successes = results.filter((r) => r.status === 'done');
    setCreatedCount(successes.length);

    if (successes.length === chosen.length) {
      // Full success — clear the captured elements, ready for the next document.
      restorePhotos([]);
      restoreNotes([]);
      if (ctx.activeDraftId) await purgeAndForgetDraft(ctx.activeDraftId).catch(() => {});
      dispatch({ type: 'SUBMIT_SUCCESS' });
      workflow.clearPersistedState();
    } else {
      // Keep the captured content so failed templates can be retried.
      dispatch({
        type: 'SUBMIT_FAILURE',
        error: successes.length > 0
          ? 'Some documents could not be generated — your captured items were kept so you can retry.'
          : 'Document generation failed — your captured items were kept so you can retry.',
      });
    }

    // No automatic document open: the notification panel re-opens itself (each
    // finished job bumps notificationSignal, which NotificationOverlayProvider
    // watches) and every completed notification links to its document. The
    // progress panel's eye button still opens the in-page viewer deliberately.
  }

  // ── Created-document viewer (debounced autosave, same as the pillar lists) ──
  const [docName, setDocName] = useState('');
  const [docContent, setDocContent] = useState('');
  const [docLoading, setDocLoading] = useState(false);
  const [docDirty, setDocDirty] = useState(false);
  const [docSaving, setDocSaving] = useState(false);
  // Git-source (ADR-033): once a doc is committed, its content lives in git and
  // is edited in the reader — this quick-view becomes READ-ONLY so it never
  // writes a stale Draft# copy that a publish would then clobber.
  const [docGit, setDocGit] = useState<{ repo: string; path: string } | null>(null);
  const autosaveTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!created) return;
    let cancelled = false;
    setDocLoading(true);
    setDocName('');
    setDocContent('');
    setDocDirty(false);
    setDocGit(null);
    host.getDoc(created.id)
      .then((doc) => {
        if (cancelled) return;
        setDocName(doc.name ?? '');
        setDocContent(doc.proposalContent);
        if (doc.gitPath && doc.gitRepo) setDocGit({ repo: doc.gitRepo, path: doc.gitPath });
      })
      .catch(() => { if (!cancelled) workflow.setError('Document created, but it could not be loaded.'); })
      .finally(() => { if (!cancelled) setDocLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, host]);

  const saveDoc = useCallback(async () => {
    // A committed doc is read-only here — editing happens in the reader (git).
    if (!created || docGit) return;
    setDocSaving(true);
    try {
      await host.updateDoc(created.id, { proposalContent: docContent });
      setDocDirty(false);
    } finally {
      setDocSaving(false);
    }
  }, [created, host, docContent, docGit]);

  // The timer reads the save function through a ref so it always saves the
  // latest content, not the closure it was scheduled with.
  const saveRef = useRef(saveDoc);
  useEffect(() => { saveRef.current = saveDoc; }, [saveDoc]);
  useEffect(() => () => {
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
  }, []);

  const handleDocChange = useCallback((content: string) => {
    setDocContent(content);
    setDocDirty(true);
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void saveRef.current();
    }, AUTOSAVE_DELAY_MS);
  }, []);

  const closeCreated = useCallback(() => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (docDirty) void saveRef.current();
    setCreated(null);
  }, [docDirty]);

  const closeResults = useCallback(() => {
    setGenJobs(null);
    setCreatedCount(0);
  }, []);

  // ── Live transcript entry data for the items panel ──
  const liveStreamingTranscript = useMemo(() => {
    if (!voice.isListening) return undefined;
    const words = liveTranscript.split(/\s+/).filter(Boolean);
    const isTruncated = words.length > LIVE_TRANSCRIPT_WORD_LIMIT;
    return {
      finalizedText: isTruncated ? words.slice(-LIVE_TRANSCRIPT_WORD_LIMIT).join(' ') : liveTranscript,
      wordCount: words.length,
      isTruncated,
      isActive: true,
    };
  }, [voice.isListening, liveTranscript]);

  const stepIndex = STEPS.findIndex((s) => s.key === ctx.step);

  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <input
        ref={fileRef}
        type="file"
        accept={UPLOAD_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleUploadedFile(file);
        }}
      />

      {/* Title with gradient underline */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Create a document</h1>
        <div className="mx-auto mt-1.5 h-0.5 w-40 rounded-full bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
      </div>

      {/* Stepper — a completed step is the way back to it; there are no back arrows. */}
      <div className="flex items-center justify-center mb-6">
        {STEPS.map((s, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <div key={s.key} className="flex items-center">
              <button
                type="button"
                disabled={!done}
                onClick={() => workflow.goToStep(s.key)}
                aria-current={active ? 'step' : undefined}
                className={cn(
                  'flex flex-col items-center rounded-xl px-1.5 py-0.5 transition-colors',
                  done && 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800',
                )}
              >
                <span className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors',
                  active ? 'bg-blue-600 text-white'
                    : done ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400',
                )}>
                  {done ? <Check className="w-4 h-4" /> : i + 1}
                </span>
                <span className={cn(
                  'mt-1 text-[11px]',
                  active ? 'text-blue-600 dark:text-blue-400 font-medium'
                    : done ? 'text-blue-600 dark:text-blue-400'
                    : 'text-slate-400',
                )}>
                  {s.label}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <div className={cn('h-px w-10 sm:w-16 mx-1 mb-4', i < stepIndex ? 'bg-blue-400' : 'bg-slate-200 dark:bg-slate-700')} />
              )}
            </div>
          );
        })}
      </div>

      {ctx.error && (
        <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <p className="flex-1">{ctx.error}</p>
          <button type="button" onClick={() => workflow.setError(null)} aria-label="Dismiss" className="shrink-0 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Step 1 — Add Items (multi-element capture) */}
      {ctx.step === 'capture' && (
        <div>
          {/* Capture type buttons — large + centered when empty, compact when items exist */}
          <div className={cn(
            'flex items-center justify-around transition-all duration-500 ease-in-out',
            isEmpty ? 'mb-8 mt-6 gap-4 flex-wrap' : 'mb-6 mt-2',
          )}>
            {METHODS.map((m) => {
              const enabled = methodEnabled(m.key);
              const listening = m.key === 'voice' && voice.isListening;
              const Icon = m.icon;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => pickMethod(m)}
                  disabled={!enabled}
                  aria-pressed={listening}
                  className={cn(
                    'group flex flex-col items-center gap-1.5 px-2 py-2 rounded-xl flex-shrink-0 transition-all duration-500 ease-in-out text-slate-500 dark:text-slate-400',
                    enabled ? m.hover : 'opacity-40',
                  )}
                >
                  <div className={cn(
                    'rounded-full flex items-center justify-center shadow-sm dark:shadow-slate-900/30 transition-all duration-500 ease-in-out group-hover:shadow-md group-active:scale-95',
                    m.bg, m.fg,
                    isEmpty ? 'w-20 h-20' : 'w-12 h-12',
                    listening && 'ring-2 ring-red-500 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-950 animate-pulse',
                  )}>
                    <Icon className={cn('transition-all duration-500 ease-in-out', isEmpty ? 'w-9 h-9' : 'w-5 h-5')} strokeWidth={1.8} />
                  </div>
                  <span className="text-sm font-medium leading-none">
                    {listening ? 'Stop' : m.label}
                  </span>
                  {!enabled && (
                    <span className="text-[9px] uppercase tracking-wide text-slate-400">N/A</span>
                  )}
                </button>
              );
            })}
          </div>

          {isEmpty && (
            <p className="text-center text-sm text-slate-500 dark:text-slate-400 mb-6">
              Capture as many items as you like — voice, photos, notes, files — then click Next to choose templates.
            </p>
          )}

          {/* Inline capture panel */}
          {activePanel === 'photo' && (
            <div className="mb-4">
              <PhotoCapturePanel onDiscard={() => setActivePanel(null)} />
            </div>
          )}
          {activePanel === 'text' && (
            <div className="mb-4">
              <ListItemCard
                icon={
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-xl">
                    <StickyNote className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                }
                onSend={() => {
                  if (ctx.note.trim()) { addNote(ctx.note.trim()); workflow.setNote(''); }
                  setActivePanel(null);
                }}
                sendDisabled={!ctx.note.trim()}
                sendLabel="Capture"
                onDiscard={() => { workflow.setNote(''); setActivePanel(null); }}
                discardLabel="Discard"
              >
                <textarea
                  value={ctx.note}
                  onChange={(e) => workflow.setNote(e.target.value)}
                  placeholder="Jot down what this document should cover…"
                  rows={3}
                  autoFocus
                  maxLength={10000}
                  className="w-full px-0 py-0 border-0 bg-transparent focus:ring-0 resize-none text-sm outline-none text-slate-700 dark:text-slate-300 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                />
              </ListItemCard>
            </div>
          )}

          {/* Captured items list */}
          <CapturedItemsPanel
            streamingTranscripts={ctx.capturedStreams}
            photos={photos}
            notes={notes}
            uploadStatuses={uploadStatuses}
            onDiscardStreamingTranscript={(id) => dispatch({ type: 'DISCARD_STREAMING_TRANSCRIPT', id })}
            onViewStreamingTranscript={openTranscript}
            onRetryUpload={retryPhotoUpload}
            onViewNote={openNote}
            onRemoveNote={removeNote}
            onRemovePhoto={removePhoto}
            onViewPhoto={openPhoto}
            onPreviewPhoto={handlePreviewPhoto}
            liveStreamingTranscript={liveStreamingTranscript}
            onStopStreaming={captureRecording}
            onDiscardLiveStreaming={discardRecording}
            streamingDuration={recordSeconds}
          />

          {/* My drafts */}
          <MyDraftsPanel
            drafts={drafts}
            isLoading={draftsLoading}
            hasAttemptedLoad={draftsHasAttemptedLoad}
            onRestore={handleRestoreDraft}
            onDelete={(id) => void deleteDraft(id)}
            isRestoring={ctx.isRestoring}
          />

          {/* Action buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            {hasItems && (
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={draftSaving}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 text-base font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
              >
                {draftSaving ? <Spinner size="sm" tone="current" /> : <Clock className="w-4 h-4" />}
                Save draft
              </button>
            )}
            {hasItems && (
              <button
                type="button"
                onClick={() => void goToTemplates()}
                className="flex flex-1 sm:flex-none sm:min-w-[220px] items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white text-base font-medium active:scale-[0.99] transition-transform"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 2 — Templates */}
      {ctx.step === 'template' && (
        <div className="space-y-5 relative">
          {/* Captured items summary */}
          <CapturedItemsPanel
            variant="summary"
            streamingTranscripts={ctx.capturedStreams}
            photos={photos}
            notes={notes}
            uploadStatuses={uploadStatuses}
          />

          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Select one or more templates. The AI analyses everything you captured and assembles one document per template — in parallel.
            </p>

            {templatesError && (
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm p-3">{templatesError}</div>
            )}

            {templatesLoading ? (
              <LoadingState size="md" />
            ) : (
              <>
                {/* Generic document — no template, AI shapes the content */}
                <TemplateRow
                  template={GENERIC_TEMPLATE}
                  selected={ctx.selectedTemplates.some((t) => t.id === GENERIC_TEMPLATE.id)}
                  onToggle={() => workflow.toggleTemplate(GENERIC_TEMPLATE)}
                />

                {GROUP_ORDER.map((groupType) => {
                  const rows = templates.filter((t) => t.docType === groupType);
                  if (!rows.length) return null;
                  return (
                    <div key={groupType} className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{GROUP_LABEL[groupType]}</p>
                      {rows.map((t) => (
                        <TemplateRow
                          key={t.id}
                          template={t}
                          selected={ctx.selectedTemplates.some((s) => s.id === t.id)}
                          onToggle={() => workflow.toggleTemplate(t)}
                          onView={() => setModal({ mode: 'view', templateId: t.id })}
                          onEdit={t.type === 'custom' ? () => setModal({ mode: 'view', templateId: t.id, startEditing: true }) : undefined}
                        />
                      ))}
                    </div>
                  );
                })}
              </>
            )}

            <button
              type="button"
              onClick={() => setModal({ mode: 'create' })}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 py-2.5 text-sm font-medium text-slate-500 hover:border-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <Plus className="h-4 w-4" /> Add template
            </button>
          </div>

          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={!canGenerate}
            className="flex w-full items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white text-base font-medium disabled:opacity-50 active:scale-[0.99] transition-transform"
          >
            {ctx.isSubmitting && <Spinner size="sm" tone="current" />}
            {ctx.isSubmitting
              ? 'Generating…'
              : ctx.selectedTemplates.length > 1
              ? `Generate ${ctx.selectedTemplates.length} documents`
              : 'Generate document'}
          </button>
        </div>
      )}

      {/* Generation progress / results */}
      {genJobs && (
        <div className="mt-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {ctx.isSubmitting ? 'Generating documents…' : `Generated ${createdCount} of ${genJobs.length}`}
            </p>
            {!ctx.isSubmitting && (
              <button type="button" onClick={closeResults} aria-label="Close results" className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="space-y-2">
            {genJobs.map((job) => (
              <div key={job.templateId} className="flex items-center gap-3 rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2">
                <span className="shrink-0">
                  {job.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    : job.status === 'failed' ? <XCircle className="w-4 h-4 text-red-500" />
                    : <Spinner size="sm" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                    {job.docName || job.templateName}
                  </span>
                  <span className="block text-xs text-slate-500 truncate">
                    {job.status === 'preparing' && 'Preparing…'}
                    {job.status === 'generating' && 'AI is analysing your captured items…'}
                    {job.status === 'creating' && 'Saving the document…'}
                    {job.status === 'done' && (job.docType === 'design' ? 'Design' : 'Requirement')}
                    {job.status === 'failed' && (job.error || 'Generation failed')}
                  </span>
                </span>
                {job.status === 'done' && job.draftId && (
                  <span className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setCreated({ id: job.draftId!, docType: job.docType })}
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                      title="Open here"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {consoleUrl && (
                      <a
                        href={consoleUrl(consoleDocumentPath(job.draftId, job.docType))}
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                        title="Open full editor"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <TemplateModal
          mode={modal.mode}
          templateId={modal.templateId}
          startEditing={modal.startEditing}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            loadTemplates();
            if (!ctx.selectedTemplates.some((t) => t.id === saved.id)) {
              workflow.setTemplates([...ctx.selectedTemplates, saved]);
            }
          }}
          onDeleted={(id) => {
            loadTemplates();
            workflow.setTemplates(ctx.selectedTemplates.filter((t) => t.id !== id));
          }}
        />
      )}

      {/* Element viewer/editor (notes, AI drafts, transcripts, photo descriptions) */}
      {viewer && (
        <DocumentModal
          item={{
            id: viewer.refId ?? 'new',
            title: viewer.title,
            subtitle: viewer.subtitle,
            content: viewer.content,
          }}
          defaultPanel={viewer.readOnly ? 'preview' : 'edit'}
          readOnly={viewer.readOnly}
          hasUnsavedChanges={viewer.dirty}
          onClose={closeViewer}
          onContentChange={(content) => setViewer((v) => (v ? { ...v, content, dirty: true } : v))}
          onSave={() => { if (viewer) { saveViewer(viewer); setViewer((v) => (v ? { ...v, dirty: false } : v)); } }}
        />
      )}

      {/* Photo preview lightbox */}
      {previewPhotoUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={closePhotoPreview}>
          <button
            type="button"
            onClick={closePhotoPreview}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/40 rounded-full transition-colors"
            aria-label="Close preview"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={previewPhotoUrl}
            alt="Photo preview"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Created-document viewer */}
      {created && (
        <DocumentModal
          item={{
            id: created.id,
            title: docName || 'New document',
            subtitle: created.docType === 'design' ? 'Design' : 'Requirement',
            content: docContent,
          }}
          isLoadingContent={docLoading}
          readOnly={docLoading || !!docGit}
          hasUnsavedChanges={docDirty}
          isSaving={docSaving}
          onClose={closeCreated}
          onContentChange={handleDocChange}
          onSave={saveDoc}
          renderFooter={() => (
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {docGit
                  ? 'Versioned in git — edit in the reader, where changes commit to GitHub.'
                  : createdCount > 1
                    ? `${createdCount} documents created — the rest are in Docs. Changes save automatically.`
                    : 'Changes save automatically.'}
              </span>
              {consoleUrl && (
                <a
                  href={consoleUrl(docGit
                    ? `/book/${docGit.repo}/${docGit.path.endsWith('.md') ? docGit.path : `${docGit.path}/proposal.md`}`
                    : consoleDocumentPath(created.id, created.docType))}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> {docGit ? 'Edit in reader' : 'Open full editor'}
                </a>
              )}
            </div>
          )}
        />
      )}
    </div>
  );
}

/** One selectable template row (with optional view/edit actions). */
function TemplateRow({
  template, selected, onToggle, onView, onEdit,
}: {
  template: TemplateMetadata;
  selected: boolean;
  onToggle: () => void;
  onView?: () => void;
  onEdit?: () => void;
}) {
  const Icon = templateIcon(template.icon);
  const color = templateColor(template.color);
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border p-3 transition-colors',
        selected ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-200 dark:border-slate-800',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={selected}
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
      >
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', color.bg)}>
          {selected ? <Check className={cn('h-5 w-5', color.fg)} /> : <Icon className={cn('h-5 w-5', color.fg)} />}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">{template.name}</span>
          <span className="block text-xs text-slate-500 line-clamp-2">{template.description}</span>
        </span>
      </button>
      {(onView || onEdit) && (
        <div className="flex shrink-0 items-center gap-0.5">
          {onView && (
            <button
              type="button"
              onClick={onView}
              title="View template"
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              title="Edit template"
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
